<!--
  Affirm's third-party ALA widget always displays a minimum of 35 USD, so we only
  render it when the price falls within the 200–1750 USD range we actually offer 
  them as a payment method in.

  Outside that range we fall back to our own "Pay in installments" messaging.

  This will be reverted once their 0% APR campaign ends.
-->

<template>
  <div v-if="!hasError">
    <AffirmThirdPartyAdvertisement
      v-if="isInAlaRange"
      :base-price
      :payment-methods="props.paymentMethods"
      :spot
    />

    <PaymentMethodsErrors
      v-else
      class="body-2"
      inner-class="flex grow items-center gap-x-8"
      :payment-methods="[affirmPaymentMethod!]"
    >
      <PaymentIcons class="self-start" :methods="[affirmPaymentMethod!]" />

      {{ i18n(translations.payInInstallments) }}

      <AffirmModal
        :base-price
        class="inline"
        :payment-methods="props.paymentMethods"
        :spot
      >
        <template #trigger="{ open, ariaLabel }">
          <RevLink
            :aria-label
            @click="
              () => {
                open()
                emit('open')
              }
            "
            >{{ i18n(translations.learnMore) }}</RevLink
          >
        </template>
      </AffirmModal>
    </PaymentMethodsErrors>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { PaymentMethodCode } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { priceToMinorUnits } from '@backmarket/utils/math/priceToMinorUnits'
import { RevLink } from '@ds/components/Link'

import {
  PaymentIcons,
  PaymentMethodsErrors,
  usePaymentMethodsWithFallback,
} from '../../../methods'
import type { PaymentAdvertisingModalProps } from '../../types/PaymentAdvertisingModalProps'
import AffirmModal from '../AffirmModal/AffirmModal.vue'
import AffirmThirdPartyAdvertisement from '../AffirmThirdPartyAdvertisement/AffirmThirdPartyAdvertisement.vue'
import translations from '../PaymentAdvertisement/PaymentAdvertisement.translations'

const AFFIRM_ALA_MIN_AMOUNT = 20000
const AFFIRM_ALA_MAX_AMOUNT = 175000

const emit = defineEmits(['open'])

const i18n = useI18n()

const props = withDefaults(defineProps<PaymentAdvertisingModalProps>(), {
  name: 'AffirmAdvertisement',
  paymentMethods: null,
})

const paymentMethods = usePaymentMethodsWithFallback(() => props.paymentMethods)

const affirmPaymentMethod = computed(() =>
  paymentMethods.value.find(
    ({ bmCode }) => bmCode === PaymentMethodCode.AFFIRM,
  ),
)

const hasError = computed(() => !affirmPaymentMethod.value)

const purchaseAmount = computed(() => priceToMinorUnits(props.basePrice))
const isInAlaRange = computed(
  () =>
    purchaseAmount.value >= AFFIRM_ALA_MIN_AMOUNT &&
    purchaseAmount.value <= AFFIRM_ALA_MAX_AMOUNT,
)
</script>
